import { platform } from '../constants/SiteVariables';

const POSTHOG_PROJECT_TOKEN = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
let initialized = false;
let lastIdentifySignature;
let lastGroupSignature;
let client;

// posthog-js crashes jsdom at module scope — must stay a lazy require
const getClient = () => {
  if (client) return client;

  const posthogModule = require('posthog-js');
  client = posthogModule.default || posthogModule;

  return client;
};

const compact = (properties) => {
  if (!properties || typeof properties !== 'object') return {};

  return Object.entries(properties).reduce((memo, [key, value]) => {
    if (value === null || value === undefined || value === '') return memo;

    memo[key] = value;
    return memo;
  }, {});
};

// Anonymous ids are UUIDs; our identified ids are 24-hex Mongo ids
const ANONYMOUS_DISTINCT_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Verification routes embed the raw email/phone in the path
// (/verify/:type/:contact), which ships in URL event properties
const scrubContactData = (value) => {
  if (typeof value !== 'string') return value;

  return value
    .replace(/[^/?&#\s]+(@|%40)[^/?&#\s]+/gi, ':redacted')
    .replace(/\/(phone|email)\/[^/?#]+/gi, '/$1/:redacted');
};

export const posthogBeforeSend = (event) => {
  if (!event) return event;

  [event.properties, event.$set, event.$set_once].forEach((bag) => {
    if (!bag) return;

    Object.keys(bag).forEach((key) => {
      if (/url|pathname|referrer/i.test(key))
        bag[key] = scrubContactData(bag[key]);
    });
  });

  // Replay payloads embed the navigation URL in rrweb meta entries (type 4)
  const snapshots = event.properties?.$snapshot_data;
  if (Array.isArray(snapshots)) {
    snapshots.forEach((snapshot) => {
      if (snapshot?.type === 4 && snapshot.data?.href) {
        snapshot.data.href = scrubContactData(snapshot.data.href);
      }
    });
  }

  return event;
};

const maskReplayInputValue = (value, element) => {
  if (!element?.classList?.contains('ph-mask')) return value;
  if (value === null || value === undefined) return value;

  return '[REDACTED]';
};

// Raw ck-web user id — must match the borrower portal's identify key
// exactly so both apps resolve to one PostHog person
const buildDistinctId = ({ user = {} }) => (user?.id ? String(user.id) : null);

// Property keys are camelCase, matching ck-frontends' person properties
const buildPersonProperties = ({
  application = {},
  company = {},
  merchant = {},
  session = {},
  user = {},
}) =>
  compact({
    applicantType: application?.applicant_type,
    applicationId: application?.id,
    companyId: company?.id,
    companyName: company?.name,
    email: user?.email,
    merchantId: merchant?.id,
    merchantPublicKey: merchant?.public_key,
    name: user?.full_name,
    sessionId: session?.id,
    sessionType: session?.session_type,
    userAccountId: user?.account_id,
    userId: user?.id,
  });

export default class PostHog {
  enabled() {
    return !!POSTHOG_PROJECT_TOKEN;
  }

  // Flags only ever arrive from a live client, so UI that holds itself back
  // until a variant lands would wait forever on a disabled one.
  active() {
    return this.enabled() && initialized;
  }

  init() {
    if (!this.enabled() || initialized) return false;

    const posthog = getClient();
    posthog.init(POSTHOG_PROJECT_TOKEN, {
      api_host: POSTHOG_HOST,
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      autocapture: true,
      // Autocaptured element text can echo rendered PII
      mask_all_text: true,
      before_send: posthogBeforeSend,
      // Replay masks only sensitive inputs tagged with PostHog's `ph-mask`
      // class plus opt-in user data rendered as DOM text
      session_recording: {
        maskAllInputs: true,
        maskInputFn: maskReplayInputValue,
        maskTextSelector: '.ph-mask-text',
      },
      debug: platform() !== 'production',
    });

    initialized = true;
    return true;
  }

  identify(context = {}) {
    if (!this.enabled() || !initialized) return false;

    const distinctId = buildDistinctId(context);
    if (!distinctId) return false;

    // Session entities are replaced on every backend sync; only forward
    // real changes
    const personProperties = buildPersonProperties(context);
    const signature = JSON.stringify([distinctId, personProperties]);
    if (signature === lastIdentifySignature) return false;
    lastIdentifySignature = signature;

    const posthog = getClient();

    // Shared browsers: posthog-js won't re-key a different identified
    // user without a reset; anonymous ids merge normally
    const currentId = posthog.get_distinct_id?.();
    const needsReset =
      !!currentId &&
      currentId !== distinctId &&
      !ANONYMOUS_DISTINCT_ID.test(currentId);
    if (needsReset) {
      posthog.reset();
      lastGroupSignature = undefined;
    }

    posthog.identify(distinctId, personProperties);
    if (needsReset) this.group(context);

    return true;
  }

  // A new session with no user must not inherit the previously identified
  // user this browser may still carry — reset so it starts anonymous
  resetStaleIdentity() {
    if (!this.enabled() || !initialized) return false;

    const posthog = getClient();
    const currentId = posthog.get_distinct_id?.();
    if (!currentId || ANONYMOUS_DISTINCT_ID.test(currentId)) return false;

    posthog.reset();
    lastIdentifySignature = undefined;
    lastGroupSignature = undefined;

    return true;
  }

  // Separate from identify() so groups attach to anonymous funnel steps too
  group(context = {}) {
    if (!this.enabled() || !initialized) return false;

    const { company = {}, merchant = {} } = context;
    if (!company?.id && !merchant?.id && !merchant?.public_key) return false;

    const signature = JSON.stringify([
      company?.id,
      merchant?.id,
      merchant?.public_key,
    ]);
    if (signature === lastGroupSignature) return false;
    lastGroupSignature = signature;

    const posthog = getClient();

    if (company?.id) {
      posthog.group(
        'company',
        `company:${company.id}`,
        compact({
          companyId: company.id,
          merchantId: merchant?.id,
          merchantPublicKey: merchant?.public_key,
        })
      );
    }

    if (merchant?.id || merchant?.public_key) {
      posthog.group(
        'merchant',
        merchant?.public_key || `merchant:${merchant.id}`,
        compact({
          merchantId: merchant?.id,
          merchantPublicKey: merchant?.public_key,
        })
      );
    }

    return true;
  }

  // Seam for product-defined events; business properties only, never PII
  capture(event, properties = {}) {
    if (!this.enabled() || !initialized) return false;

    const posthog = getClient();
    posthog.capture(event, compact(properties?.toJS?.() ?? properties));

    return true;
  }

  // null until flags have loaded (or when PostHog is disabled)
  isFeatureEnabled(flag) {
    if (!this.enabled() || !initialized) return null;

    return getClient().isFeatureEnabled(flag) ?? null;
  }

  // Preserve multivariate values; a boolean check cannot distinguish test/control.
  getFeatureFlag(flag) {
    if (!this.enabled() || !initialized) return null;

    try {
      return getClient().getFeatureFlag(flag) ?? null;
    } catch {
      return null;
    }
  }

  onFeatureFlags(callback) {
    if (!this.enabled() || !initialized) return undefined;

    return getClient().onFeatureFlags(callback);
  }
}
